import { Box, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerProductValidation } from '../hooks/useServerProductValidation';
import { arePricesDirty, toPricePayload } from '../hooks/useUpsertPriceForm';
import { useUpsertProductForm } from '../hooks/useUpsertProductForm';
import type { ProductDto, UpsertProductDto } from '../types/ProductDto';
import { UpsertProductForm } from './UpsertProductForm';

interface EditProductModalProps {
	close: () => void;
	isOpened: boolean;
	updateProductRecord: (product: ProductDto) => void;
	product: UpsertProductDto;
}

export function EditProductModal({
	isOpened,
	close,
	updateProductRecord,
	product,
}: EditProductModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertProductForm(product);

	const newProductMutation = mutate<UpsertProductDto, ProductDto>(
		`${organisation}/product/edit`,
		{
			...inputs,
			prices: arePricesDirty(form, 'prices.')
				? toPricePayload(inputs.prices)
				: undefined,
		},
	);

	useServerProductValidation(newProductMutation, form);

	const resetAndClose = () => {
		form.reset();

		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (newProductMutation.data?.value) {
			updateProductRecord(newProductMutation.data.value);

			form.setInitialValues({
				...newProductMutation.data.value,
				prices: inputs.prices ?? {},
			});
			resetAndClose();
		}
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Product'} />}
				withCloseButton={true}
				size={'xl'}
				centered
			>
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Product Edited!'}
					body={`"${inputs.skuid}" has been edited. Click Ok to continue...`}
				/>
				<Box pos={'relative'}>
					<LoadingOverlay visible={newProductMutation.isLoading} />
					<Space h={6} />
					<form
						onSubmit={form.onSubmit(async () => {
							await newProductMutation.mutateAsync().then((value) => {
								if (value.isValid) {
									openSuccessModal();
								}
							});
						})}
					>
						<UpsertProductForm form={form} isEdit />
						<FormButtons close={resetAndClose} />
					</form>
				</Box>
			</Modal>
		</>
	);
}
